/**
 * Firebase Authentication client.
 *
 * Loaded on demand: the SDK is large and only the sign-in screens need it, so
 * it stays out of the main bundle. Configuration comes from the server at
 * /api/auth/config, which keeps the deployment's project settings in one place
 * instead of being baked into the build.
 */
import { api } from './api';

export type FirebaseConfig = {
  enabled: boolean;
  project_id: string;
  api_key: string;
  auth_domain: string;
  providers: string[];
  /** Whether Firebase itself offers email/password sign-in. */
  email_password_enabled: boolean;
  /** Measurement configuration. Separate from sign-in because it is a
   *  separate decision, and off unless the deployment sets both ids. */
  analytics?: {
    enabled: boolean;
    app_id: string;
    measurement_id: string;
  };
};

export type AuthConfig = {
  firebase: FirebaseConfig;
  password_sign_in_enabled: boolean;
  registration_enabled: boolean;
  /** The published Terms the sign-up page must submit back, so the stored
   *  acceptance names the wording that was on screen. */
  terms_version: string;
  terms_url: string;
  privacy_url: string;
};

let configPromise: Promise<AuthConfig> | null = null;

export function authConfig(): Promise<AuthConfig> {
  if (!configPromise) {
    configPromise = api.get<AuthConfig>('/auth/config').catch(() => ({
      firebase: {
        enabled: false, project_id: '', api_key: '', auth_domain: '',
        providers: [], email_password_enabled: false,
        analytics: { enabled: false, app_id: '', measurement_id: '' },
      },
      password_sign_in_enabled: true,
      registration_enabled: true,
      // Empty rather than invented: the server decides what the current terms
      // are, and a guessed version would be recorded as an acceptance of
      // something nobody published.
      terms_version: '',
      terms_url: '/terms',
      privacy_url: '/privacy',
    }));
  }
  return configPromise;
}

type AuthModule = {
  auth: import('firebase/auth').Auth;
  mod: typeof import('firebase/auth');
};

let authPromise: Promise<AuthModule> | null = null;

async function firebaseAuth(): Promise<AuthModule> {
  if (!authPromise) {
    authPromise = (async () => {
      const config = (await authConfig()).firebase;
      if (!config.enabled || !config.api_key) {
        throw new Error('Firebase sign-in is not configured for this deployment.');
      }
      const [{ initializeApp, getApps }, mod] = await Promise.all([
        import('firebase/app'),
        import('firebase/auth'),
      ]);
      const app = getApps()[0] ?? initializeApp({
        apiKey: config.api_key,
        authDomain: config.auth_domain,
        projectId: config.project_id,
      });
      const auth = mod.getAuth(app);
      // Sessions live in our own cookie, so the SDK does not need to persist
      // anything beyond the current tab.
      await mod.setPersistence(auth, mod.inMemoryPersistence);
      return { auth, mod };
    })();
  }
  return authPromise;
}

/** Exchange a Firebase ID token for a platform session cookie. */
/** Acceptance of the published Terms, carried through to account creation. */
export type TermsAcceptance = { accepted: boolean; version: string };

async function exchange(idToken: string, organizationName?: string,
                        terms?: TermsAcceptance) {
  return api.post('/auth/firebase/session', {
    id_token: idToken,
    organization_name: organizationName,
    // Only meaningful when this call creates the account; the server ignores
    // it for an existing user and requires it when there is none.
    accepted_terms: terms?.accepted,
    terms_version: terms?.version,
  });
}

export async function signInWithPassword(email: string, password: string,
                                         organizationName?: string) {
  const { auth, mod } = await firebaseAuth();
  const credential = await mod.signInWithEmailAndPassword(auth, email, password);
  return exchange(await credential.user.getIdToken(), organizationName);
}

export async function registerWithPassword(email: string, password: string,
                                           name: string, organizationName?: string,
                                           terms?: TermsAcceptance) {
  const { auth, mod } = await firebaseAuth();
  const credential = await mod.createUserWithEmailAndPassword(auth, email, password);
  if (name) await mod.updateProfile(credential.user, { displayName: name });
  // Send the verification email; linking to an existing local account later
  // requires a verified address.
  await mod.sendEmailVerification(credential.user).catch(() => undefined);
  return exchange(await credential.user.getIdToken(true), organizationName, terms);
}

export async function signInWithProvider(providerId: string,
                                         organizationName?: string,
                                         terms?: TermsAcceptance) {
  const { auth, mod } = await firebaseAuth();
  const provider =
    providerId === 'google' || providerId === 'google.com'
      ? new mod.GoogleAuthProvider()
      : providerId === 'microsoft' || providerId === 'microsoft.com'
        ? new mod.OAuthProvider('microsoft.com')
        : providerId === 'apple' || providerId === 'apple.com'
          ? new mod.OAuthProvider('apple.com')
          : null;
  if (!provider) throw new Error(`Unsupported sign-in provider: ${providerId}`);
  const credential = await mod.signInWithPopup(auth, provider);
  return exchange(await credential.user.getIdToken(), organizationName, terms);
}

/**
 * Passwordless sign-in by email link.
 *
 * Firebase sends a one-time link; opening it on the same device completes
 * sign-in. Two details are not optional:
 *
 *   * The address is remembered locally between sending and returning,
 *     because Firebase requires it to complete the exchange and will
 *     otherwise prompt for it again. It is the address the person just
 *     typed — nothing secret — and it is cleared once used.
 *   * The link in the email **is** the credential for that exchange. It is
 *     single use, it is bound to this origin, and the token it carries is
 *     consumed the moment the sign-in completes.
 */
const EMAIL_LINK_KEY = 'esign.auth.emailLink';

export async function sendSignInLink(email: string, organizationName?: string,
                                     terms?: TermsAcceptance) {
  const { auth, mod } = await firebaseAuth();
  await mod.sendSignInLinkToEmail(auth, email, {
    // Back to this origin, where `completeSignInLink` finishes the exchange.
    //
    // No query parameter of our own. `link` in particular is reserved: the
    // SDK's link parser treats a `link` parameter as a nested deep link and
    // parses *that* for the code, so a continue URL carrying `?link=1` makes
    // every real sign-in link undetectable. The SDK recognises the return by
    // the `mode`, `oobCode` and `apiKey` parameters it appends itself, so
    // there is nothing for us to mark.
    url: `${window.location.origin}/login`,
    handleCodeInApp: true,
  });
  try {
    // The acceptance is carried with the address because the link is
    // completed on a later page load, where there is no form to collect it
    // from -- and this flow creates the account on first use.
    localStorage.setItem(EMAIL_LINK_KEY, JSON.stringify({
      email, organizationName: organizationName || '',
      termsVersion: terms?.accepted ? terms.version : '',
    }));
  } catch {
    // Storage unavailable; `completeSignInLink` will ask for the address.
  }
}

/** Whether the current URL is a sign-in link waiting to be completed. */
export async function isSignInLink(url: string = window.location.href) {
  try {
    const { auth, mod } = await firebaseAuth();
    return mod.isSignInWithEmailLink(auth, url);
  } catch {
    return false;
  }
}

/** The address the link was sent to, if this browser still remembers it. */
export function rememberedLinkEmail():
    { email: string; organizationName: string; termsVersion?: string } | null {
  try {
    const raw = localStorage.getItem(EMAIL_LINK_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export async function completeSignInLink(email: string,
                                         organizationName?: string,
                                         terms?: TermsAcceptance) {
  const { auth, mod } = await firebaseAuth();
  const credential = await mod.signInWithEmailLink(auth, email,
                                                   window.location.href);
  try {
    localStorage.removeItem(EMAIL_LINK_KEY);
  } catch {
    // Nothing to clear.
  }
  return exchange(await credential.user.getIdToken(), organizationName, terms);
}


export async function sendPasswordReset(email: string) {
  const { auth, mod } = await firebaseAuth();
  await mod.sendPasswordResetEmail(auth, email);
}

export async function firebaseSignOut() {
  if (!authPromise) return;
  try {
    const { auth, mod } = await firebaseAuth();
    await mod.signOut(auth);
  } catch {
    // Signing out of our own session is what matters; ignore SDK errors.
  }
}

/** Turn a Firebase SDK error into something worth showing a person. */
export function describeFirebaseError(error: unknown): string {
  const code = (error as { code?: string })?.code || '';
  const messages: Record<string, string> = {
    'auth/invalid-email': 'That email address does not look right.',
    'auth/user-disabled': 'That account has been disabled.',
    'auth/user-not-found': 'Email or password is incorrect.',
    'auth/wrong-password': 'Email or password is incorrect.',
    'auth/invalid-credential': 'Email or password is incorrect.',
    'auth/email-already-in-use': 'An account already exists for that email address.',
    'auth/weak-password': 'Choose a longer password.',
    'auth/popup-closed-by-user': 'The sign-in window was closed before finishing.',
    'auth/popup-blocked': 'Your browser blocked the sign-in window. Allow pop-ups and try again.',
    'auth/cancelled-popup-request': 'Another sign-in window is already open.',
    'auth/network-request-failed': 'We could not reach the sign-in service. Check your connection.',
    'auth/too-many-requests': 'Too many attempts. Wait a few minutes and try again.',
    'auth/operation-not-allowed': 'That sign-in method is not enabled for this deployment.',
    'auth/unauthorized-domain': 'This site is not an authorized domain in the Firebase project.',
    // Email-link sign-in. A link is single use and short lived, so the
    // commonest cause of each of these is an ordinary one — the link was
    // already used, or it sat in an inbox for a day.
    'auth/invalid-action-code':
      'That sign-in link has already been used or has expired. Ask for a new one.',
    'auth/expired-action-code':
      'That sign-in link has expired. Ask for a new one.',
    'auth/invalid-email-link':
      'That does not look like a valid sign-in link. Ask for a new one.',
    'auth/missing-email':
      'Enter the address the sign-in link was sent to.',
  };
  if (messages[code]) return messages[code];
  const message = (error as { message?: string })?.message;
  return message || 'Sign-in failed. Try again.';
}
